/**
 * superAdminApi.js — Thin axios wrapper for the Super Admin / Platform endpoints.
 * All requests prefix /api/superadmin and auto-attach JWT via shared axios instance.
 */
import axios from 'axios';
import { useAuthStore } from '@/lib/store';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const getToken = () => {
  let token = useAuthStore.getState().token;
  if (!token) {
    try {
      const persisted = localStorage.getItem('eventflow-auth');
      if (persisted) token = JSON.parse(persisted)?.state?.token;
    } catch (_) {}
  }
  return token;
};

const request = async (method, path, body = null, params = null) => {
  const token = getToken();
  const url = `${BACKEND}/api/superadmin${path}`;
  const res = await axios({
    method,
    url,
    data: body || undefined,
    params: params || undefined,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.data;
};

export const superAdminApi = {
  // Dashboard
  dashboard: () => request('get', '/dashboard'),

  // Plans
  listPlans: (include_inactive = false) => request('get', '/plans', null, { include_inactive }),
  createPlan: (p) => request('post', '/plans', p),
  updatePlan: (id, p) => request('put', `/plans/${id}`, p),
  deletePlan: (id) => request('delete', `/plans/${id}`),
  seedPlans: () => request('post', '/plans/seed-defaults'),

  // Tenants
  listTenants: (params) => request('get', '/tenants', null, params),
  createTenant: (t) => request('post', '/tenants', t),
  tenantDetail: (id) => request('get', `/tenants/${id}/detail`),
  changeTenantStatus: (id, status, reason) => request('post', `/tenants/${id}/status`, { status, reason }),
  subscribe: (id, data) => request('post', `/tenants/${id}/subscribe`, data),
  cancelSubscription: (id) => request('post', `/tenants/${id}/cancel`),
  getModules: (id) => request('get', `/tenants/${id}/modules`),
  setModules: (id, modules_enabled) => request('put', `/tenants/${id}/modules`, { modules_enabled }),
  modulesCatalog: () => request('get', '/modules/catalog'),
  impersonate: (id) => request('post', `/tenants/${id}/impersonate`),

  // Applications
  listApplications: (status = null) => request('get', '/applications', null, status ? { status } : null),
  reviewApplication: (id, data) => request('post', `/applications/${id}/review`, data),

  // Billing
  listInvoices: (params) => request('get', '/invoices', null, params),
  createInvoice: (data) => request('post', '/invoices', data),
  recordPayment: (data) => request('post', '/payments', data),

  // Platform Settings (Phase 2)
  getPlatformSettings: () => request('get', '/platform-settings'),
  updatePlatformSettings: (data) => request('put', '/platform-settings', data),

  // Real-time alert integrations (Phase 5)
  testAlertWebhook: (webhook_url) => request('post', '/alerts/test-webhook', { webhook_url }),
  previewAdminAlert: () => request('post', '/alerts/preview'),

  // Tenant oversight (Phase 2)
  tenantSettingsSummary: (id) => request('get', `/tenants/${id}/settings-summary`),

  // Impersonation (Phase 2)
  activeImpersonations: () => request('get', '/impersonate/active'),
  endImpersonation: (sid) => request('post', `/impersonate/${sid}/end`),

  // Support Desk (Phase 3)
  listTickets: (params) => request('get', '/tickets', null, params),
  createTicket: (data) => request('post', '/tickets', data),
  ticketDetail: (id) => request('get', `/tickets/${id}`),
  respondTicket: (id, body) => request('post', `/tickets/${id}/respond`, { body }),
  changeTicketStatus: (id, status, note = '') => request('put', `/tickets/${id}/status`, { status, note }),
  ticketsStats: () => request('get', '/tickets-stats'),

  // Broadcasts (Phase 3)
  listBroadcasts: () => request('get', '/broadcasts'),
  createBroadcast: (data) => request('post', '/broadcasts', data),
  deleteBroadcast: (id) => request('delete', `/broadcasts/${id}`),
  tenantBroadcasts: (tenantId, unread_only = false) => request('get', `/tenants/${tenantId}/broadcasts`, null, { unread_only }),
  markBroadcastSeen: (tenantId, bcId) => request('post', `/tenants/${tenantId}/broadcasts/${bcId}/seen`),

  // CMS Oversight (Phase 3)
  tenantCmsSummary: (id) => request('get', `/tenants/${id}/cms-summary`),

  // Analytics (Phase 4)
  revenueAnalytics: () => request('get', '/analytics/revenue'),
  impersonateJwt: (tenantId) => request('post', `/tenants/${tenantId}/impersonate-jwt`),

  // Audit
  audit: (params) => request('get', '/audit', null, params),
};
